import { datadogRum, RumEvent } from '@datadog/browser-rum';
import { reactPlugin } from '@datadog/browser-rum-react';
import { DATADOG_RUM_APPLICATION_ID, DATADOG_RUM_CLIENT_TOKEN, DATADOG_RUM_ENABLED, DATADOG_RUM_VERSION, DEPLOY_ENV } from 'shared/config';

const dynamicPathSegmentPattern = /^(?:\d+|[0-9a-f]{24}|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i;

const sanitizeUrl = (value: string): string => {
  try {
    const url = new URL(value, window.location.origin);
    url.pathname = url.pathname
      .split('/')
      .map((segment) => (dynamicPathSegmentPattern.test(segment) ? ':id' : segment))
      .join('/');
    url.search = '';
    url.hash = '';
    return url.toString();
  } catch (_) {
    return value.split(/[?#]/, 1)[0];
  }
};

export const sanitizeDatadogRumEvent = (event: RumEvent): boolean => {
  const { view } = event;
  if (view?.url) {
    view.url = sanitizeUrl(view.url);
  }
  if (view?.referrer) {
    view.referrer = sanitizeUrl(view.referrer);
  }

  if (event.type === 'resource') {
    const { resource } = event;
    resource.url = sanitizeUrl(resource.url);
  }

  if (event.type === 'error' && event.error.resource?.url) {
    const { resource } = event.error;
    resource.url = sanitizeUrl(resource.url);
  }

  return true;
};

export const setDatadogRumUser = (userId: string | number, email: string): void => {
  if (!datadogRum.getInitConfiguration()) {
    return;
  }

  datadogRum.setUser({ id: String(userId), email });
};

export const endDatadogRumSession = (): void => {
  if (!datadogRum.getInitConfiguration()) {
    return;
  }

  datadogRum.stopSession();
  datadogRum.clearUser();
};

export const initializeDatadogRum = (): boolean => {
  if (!DATADOG_RUM_ENABLED || !DATADOG_RUM_APPLICATION_ID || !DATADOG_RUM_CLIENT_TOKEN) {
    return false;
  }

  if (datadogRum.getInitConfiguration()) {
    return true;
  }

  try {
    datadogRum.init({
      applicationId: DATADOG_RUM_APPLICATION_ID,
      clientToken: DATADOG_RUM_CLIENT_TOKEN,
      site: 'datadoghq.com',
      service: 'scriptsure',
      env: DEPLOY_ENV,
      version: DATADOG_RUM_VERSION,
      sessionSampleRate: 100,
      sessionReplaySampleRate: 20,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask',
      enablePrivacyForActionName: true,
      beforeSend: sanitizeDatadogRumEvent,
      plugins: [reactPlugin({ router: false })],
    });

    return true;
  } catch (error) {
    console.error('Failed to initialize Datadog RUM:', error);
    return false;
  }
};
